import { usePersistentValue } from './usePersistentState';
import { cmpVal } from '../utils/personTable';

/* Tri de table standard : colonne + direction persistées (sessionStorage),
   clic sur la colonne active = inversion de direction.
   `sortRows` trie une copie (nulls last, numeric-first via cmpVal).

   Le hook porte AUSSI l'ordre d'affichage d'un nom de personne (NOM Prénom ou
   Prénom NOM), parce que les deux ne se séparent pas : ce qu'on lit en premier
   est ce sur quoi on trie. Toute table à colonne « Collaborateur » consomme
   `nameOrder` (pour <PersonName order>), `personSortKey` (la clé de tri de la
   colonne) et `flipNameOrder` (la bascule ⇄), via <PersonTh sort={…}>. */
export function useTableSort(storageKey, defaultCol, defaultDir = 'asc') {
  const [sortCol, setSortCol] = usePersistentValue(`${storageKey}_col`, defaultCol);
  const [sortDir, setSortDir] = usePersistentValue(`${storageKey}_dir`, defaultDir);

  function handleSort(col) {
    if (sortCol === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }

  const person = usePersonNameOrder(storageKey, sortCol, setSortCol);

  function sortRows(rows, getVal) {
    const arr = [...rows];
    arr.sort((a, b) => {
      const c = getVal ? cmpVal(getVal(a, sortCol), getVal(b, sortCol)) : cmpVal(a[sortCol], b[sortCol]);
      return sortDir === 'asc' ? c : -c;
    });
    return arr;
  }

  return {
    sortCol, sortDir, handleSort, sortRows, setSortCol, setSortDir,
    ...person,
  };
}

/* Ordre d'affichage d'un nom de personne, isolé pour les tables qui portent
   encore leur tri à la main (elles n'appellent pas useTableSort). Inverser
   l'ordre déplace le tri sur l'autre partie du nom quand c'est elle qu'on
   triait — on trie sur ce qu'on lit ; un tri posé sur une autre colonne
   (salaire, date) n'est pas touché. */
export function usePersonNameOrder(storageKey, sortCol, setSortCol) {
  const [nameOrder, setNameOrder] = usePersistentValue(`${storageKey}_nameorder`, 'ln');
  const personSortKey = nameOrder === 'fn' ? 'first_name_sort' : 'last_name_sort';
  function flipNameOrder() {
    const next = nameOrder === 'fn' ? 'ln' : 'fn';
    setNameOrder(next);
    if (sortCol === 'first_name_sort' || sortCol === 'last_name_sort') {
      setSortCol(next === 'fn' ? 'first_name_sort' : 'last_name_sort');
    }
  }
  return { nameOrder, personSortKey, flipNameOrder };
}
